import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Layers, BarChart2, Inbox, MessageCircle, Phone } from "lucide-react";
import axios from "axios";
import gmailLogo from "../assets/gmail.svg";
import WhatsAppTemplateDevModal from "./sequencing/WhatsAppTemplateDevModal";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const ENTRY_POINTS = [
    {
        id: "email-templates",
        icon: Mail,
        title: "Email Templates",
        description: "Manage global email templates used across outreach.",
        path: "/org-admin/sourcing/templates",
    },
    {
        id: "sequences",
        icon: Layers,
        title: "Create Outreach",
        description: "Build a multi-email outreach with pre-filled templates.",
        path: "/org-admin/sourcing/sequences",
    },
    {
        id: "email-tracker",
        icon: BarChart2,
        title: "Email Tracker",
        description: "View logs of all emails sent to candidates across outreach.",
        path: "/org-admin/sourcing/emailtracker",
    },
    {
        id: "call-tracker",
        icon: Phone,
        title: "Call Tracker",
        description: "View the status and outcome of AI calls to candidates.",
        path: "/org-admin/sourcing/calltracker",
    },
    {
        id: "screening-responses",
        icon: Inbox,
        title: "Screening Responses",
        description: "See who's interested and qualified, then refine your shortlist.",
        path: "/org-admin/sourcing/screening-responses",
    },
];

const EntryPoint = () => {
    const navigate = useNavigate();
    const [gmailAccount, setGmailAccount] = useState(null);
    const [showWaDevModal, setShowWaDevModal] = useState(false);

    const recruiterId = localStorage.getItem("org_admin_id");
    const orgId = localStorage.getItem("current_org_id");

    useEffect(() => {
        if (!recruiterId || !orgId) return;
        axios
            .get(`${SOURCING_URL}/auth/email-account`, { params: { created_by: recruiterId, org_id: orgId } })
            .then(({ data }) => setGmailAccount(data.connected ? data : false))
            .catch(() => setGmailAccount(false));
    }, [recruiterId, orgId]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-base font-semibold text-gray-800 dark:text-white">Entry Point</h1>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-10">
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Select an action to get started.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ENTRY_POINTS.map(({ id, icon: Icon, title, description, path }) => (
                        <button
                            key={id}
                            onClick={() => navigate(path)}
                            className="bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-600 rounded-xl px-6 py-5 flex items-start gap-4 text-left hover:border-purple-300 hover:shadow-sm transition-all"
                        >
                            <div className="mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                                style={{ background: "linear-gradient(to bottom right, #f9a8d4, #93c5fd)" }}>
                                <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-800 dark:text-white">{title}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{description}</p>
                            </div>
                        </button>
                    ))}

                    {/* Account Connection card */}
                    <button
                        onClick={() => navigate("/org-admin/sourcing/emailsettings")}
                        className="bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-600 rounded-xl px-6 py-5 flex items-start gap-4 text-left hover:border-purple-300 hover:shadow-sm transition-all"
                    >
                        <img src={gmailLogo} alt="Gmail" className="mt-0.5 w-9 h-9 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-white">Account Connection</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Manage your connected email accounts for outreach.</p>
                            <div className="mt-2">
                                {gmailAccount === null ? null : gmailAccount ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 text-xs text-green-700 dark:text-green-300 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                        Gmail · {gmailAccount.email}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-white/[0.03] text-xs text-gray-500 dark:text-gray-400 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                        Not connected
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>
                </div>

                {/* Developer utility — seed approved Meta WhatsApp templates into the DB. */}
                <div className="mt-8 pt-4 border-t border-dashed border-gray-200 dark:border-gray-600">
                    <button
                        onClick={() => setShowWaDevModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:border-green-300 hover:text-green-600 transition-all"
                    >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Dev: Register WhatsApp Template
                    </button>
                </div>
            </div>

            <WhatsAppTemplateDevModal isOpen={showWaDevModal} onClose={() => setShowWaDevModal(false)} />
        </div>
    );
};

export default EntryPoint;
